import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePricingRulesUpdateMutation } from 'api';
import { Form, FormInstance, FormProps, notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { WebsiteDataType } from 'feature/brandTabs/pricingAndSuppliers/pricing/edit/WebsiteItemCard';
import { generateUniqueId, getUpdatedPayload } from 'utils/helpers';
import { useWatch } from 'antd/es/form/Form';
import { PricingRuleType } from 'typings/form/brand/pricingAndSupplier';
import { IEditPricing } from 'feature/brandTabs/pricingAndSuppliers/pricing/edit/index';

export const useEditPricing = (props: IEditPricing) => {
  const { onModalStateChange, modalState, dataToEdit } = props;
  const [websites, setWebsites] = useState<WebsiteDataType[]>([]);
  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);
  const [editId, setEditId] = useState<string | null>(null);

  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const website = useWatch('website', form);
  const [updatePricingRules, { isLoading }] = usePricingRulesUpdateMutation();

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const clearFieldValidation = useCallback((form: FormInstance, field: string) => {
    const hasError = form.getFieldError(field)?.length;
    if (hasError) {
      form.setFields([
        {
          name: field,
          errors: [], // Clears the error for filed
        },
      ]);
    }
  }, []);

  const onWebsiteItemCancel = useCallback(() => setEditId(null), []);
  const onWebsiteItemEdit = useCallback(
    (id: string) => {
      setEditId(id);
      const websiteItemData = websites.find((_website) => _website.id === id);
      if (websiteItemData) {
        form.setFieldsValue({
          edit_website: websiteItemData.label,
        });
      }
    },
    [websites, form]
  );

  const onWebsiteItemSave = useCallback(
    async (id: string) => {
      try {
        await form.validateFields(['edit_website']);
        const { edit_website } = form.getFieldsValue(['edit_website']);
        setWebsites((prevState) =>
          prevState.map((website) => {
            if (website.id === id) {
              return {
                ...website,
                label: edit_website,
              };
            } else {
              return website;
            }
          })
        );

        form.resetFields(['edit_website']);
        setEditId(null);
      } catch {
        // handle catch
      }
    },
    [form]
  );
  const onWebsiteItemRemove = useCallback((id: string) => {
    setWebsites((prevState) => prevState.filter((_website) => _website.id !== id));
    setDisableSaveBtn(false);
  }, []);

  const onWebsiteSave = useCallback(async () => {
    try {
      await form.validateFields(['website']);
      const { website } = form.getFieldsValue(['website']);
      setWebsites((prevState) => [
        ...prevState,
        {
          label: website,
          id: generateUniqueId(),
        },
      ]);
      form.resetFields(['website']);
    } catch {
      // handle catch
    }
  }, [form]);

  const onWebsiteCancel = useCallback(() => {
    form.resetFields(['website']);
  }, [form]);

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
    setDisableSaveBtn(true);
  }, [form, onModalStateChange]);

  const onFinish: FormProps<PricingRuleType>['onFinish'] = useCallback(
    async (values: PricingRuleType) => {
      const payload = getUpdatedPayload(values, dataToEdit);
      payload.brandId = id as string;
      payload.websites = websites.map((_item) => _item.label);
      // TODO
      // payload.websites = websites?.length ? websites.map((_item) => _item.label) : undefined;
      try {
        const { data, error }: Record<string, any> = await updatePricingRules(payload);
        if (data?.success) {
          openNotificationWithIcon({
            type: 'success',
            message: 'Pricing rules was successfully updated.',
          });
          onCancel();
        } else if (error?.data?.success === false) {
          Object.values(error?.data?.data || {}).forEach((message) => {
            openNotificationWithIcon({
              type: 'error',
              message: message as string,
            });
          });
        } else if (error?.data?.error) {
          openNotificationWithIcon({
            type: 'error',
            message: 'Something went wrong!',
          });
        }
      } catch (err) {
        console.error('Error updating user pricing rules:', err);
        openNotificationWithIcon({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.',
        });
      }
    },

    [updatePricingRules, openNotificationWithIcon, id, onCancel, websites, dataToEdit]
  );

  useEffect(() => {
    if (modalState && dataToEdit) {
      form.setFieldsValue(dataToEdit);
    }
    if (dataToEdit?.websites && modalState) {
      const websites = dataToEdit.websites.map((_item: any) => ({ label: _item, id: generateUniqueId() }));
      setWebsites(websites);
    }
  }, [modalState, form, dataToEdit]);

  return {
    contextHolder,
    form,
    isLoading,
    isChanged: !!website,
    disableSaveBtn,
    editId,
    websites,
    clearFieldValidation,
    onWebsiteItemCancel,
    onWebsiteItemSave,
    onWebsiteCancel,
    onWebsiteItemEdit,
    onWebsiteItemRemove,
    onWebsiteSave,
    onFinish,
    onCancel,
  };
};
